import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Loader2, AlertCircle, CheckCircle, Trash2 } from 'lucide-react';

interface ImageUploadDialogProps {
  images?: string[];
  onImagesChange?: (images: string[]) => void;
  maxCount?: number;
  maxSizeMB?: number;
  disabled?: boolean;
  onSave?: () => void;
  onDeleteRequest?: (index: number) => void;
}

interface UploadStatus {
  id: string;
  status: 'compressing' | 'uploading' | 'success' | 'error';
  message: string;
}

export default function ImageUploadDialog({
  images: externalImages,
  onImagesChange,
  maxCount = 10,
  maxSizeMB = 5,
  disabled = false,
  onSave,
  onDeleteRequest
}: ImageUploadDialogProps) {
  const [internalImages, setInternalImages] = useState<string[]>([]);
  const images = (externalImages ?? []).length > 0 ? (externalImages ?? []) : internalImages;
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isProcessingRef = useRef(false);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  const minCompressSize = 512 * 1024;
  const maxDimension = 1200;
  const targetQuality = 0.75;
  const maxOutputSize = 512 * 1024;

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const addStatus = (status: UploadStatus) => {
    setUploadStatuses(prev => [...prev, status]);
  };

  const removeStatus = (id: string) => {
    setUploadStatuses(prev => prev.filter(s => s.id !== id));
  };

  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (file.type === 'image/bmp') {
        reject(new Error('BMP格式不支持，请转换为JPG/PNG后上传'));
        return;
      }

      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('无法创建 canvas 上下文'));
        return;
      }

      img.onload = () => {
        let { width, height } = img;
        if (width > maxDimension) {
          height = Math.round((height * maxDimension) / width);
          width = maxDimension;
        }
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        let quality = targetQuality;
        let result = canvas.toDataURL('image/webp', quality);
        while (result.length > maxOutputSize * 1.33 && quality > 0.3) {
          quality -= 0.05;
          result = canvas.toDataURL('image/webp', quality);
        }
        resolve(result);
      };
      img.onerror = () => reject(new Error('图片加载失败'));
      const reader = new FileReader();
      reader.onload = (e) => { img.src = e.target?.result as string; };
      reader.onerror = () => reject(new Error('文件读取失败'));
      reader.readAsDataURL(file);
    });
  };

  const processFile = async (file: File, statusId: string): Promise<string | null> => {
    if (!file.type.startsWith('image/')) {
      addStatus({ id: statusId, status: 'error', message: '格式不支持，请上传图片文件' });
      setTimeout(() => removeStatus(statusId), 3000);
      return null;
    }
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      addStatus({ id: statusId, status: 'error', message: '仅支持 JPG、PNG、WebP、GIF 格式' });
      setTimeout(() => removeStatus(statusId), 3000);
      return null;
    }
    try {
      addStatus({ id: statusId, status: 'compressing', message: '正在压缩处理...' });
      const compressed = await compressImage(file);
      const sizeInMB = (compressed.length * 0.75) / (1024 * 1024);
      if (sizeInMB > maxSizeMB) {
        addStatus({ id: statusId, status: 'error', message: `图片过大(${sizeInMB.toFixed(1)}MB)，请重新上传` });
        setTimeout(() => removeStatus(statusId), 3000);
        return null;
      }
      addStatus({ id: statusId, status: 'success', message: '上传成功，已自动保存' });
      setTimeout(() => removeStatus(statusId), 2000);
      return compressed;
    } catch (error) {
      addStatus({ id: statusId, status: 'error', message: '图片处理失败，请重试' });
      setTimeout(() => removeStatus(statusId), 3000);
      return null;
    }
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || disabled || isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsUploading(true);
    const currentImages = images ?? [];
    const remainingSlots = maxCount - currentImages.length;
    if (remainingSlots <= 0) {
      alert(`已达到最大上传数量（${maxCount}张）`);
      isProcessingRef.current = false;
      setIsUploading(false);
      return;
    }
    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    const newImages: string[] = [];
    for (const file of filesToProcess) {
      const statusId = generateId();
      addStatus({ id: statusId, status: 'uploading', message: '上传中...' });
      const result = await processFile(file, statusId);
      if (result) newImages.push(result);
    }
    if (newImages.length > 0) {
      const updatedImages = [...currentImages, ...newImages];
      setInternalImages(updatedImages);
      onImagesChange?.(updatedImages);
      onSave?.();
    }
    isProcessingRef.current = false;
    setIsUploading(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
      e.target.value = '';
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [images, maxCount, disabled]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isUploading) setIsDragging(true);
  }, [disabled, isUploading]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handlePaste = useCallback((e: ClipboardEvent) => {
    if (disabled || isProcessingRef.current) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      const dataTransfer = new DataTransfer();
      imageFiles.forEach(file => dataTransfer.items.add(file));
      handleFiles(dataTransfer.files);
    }
  }, [disabled, images, maxCount]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste as EventListener);
    return () => document.removeEventListener('paste', handlePaste as EventListener);
  }, [handlePaste]);

  const handleDeleteClick = useCallback((index: number) => {
    if (onDeleteRequest) {
      onDeleteRequest(index);
    } else {
      setShowDeleteConfirm(index);
    }
  }, [onDeleteRequest]);

  const confirmDelete = useCallback(() => {
    if (showDeleteConfirm !== null) {
      const currentImages = images ?? [];
      const newImages = currentImages.filter((_, i) => i !== showDeleteConfirm);
      setInternalImages(newImages);
      onImagesChange?.(newImages);
      onSave?.();
      setShowDeleteConfirm(null);
    }
  }, [showDeleteConfirm, images, onImagesChange, onSave]);

  const cancelDelete = useCallback(() => {
    setShowDeleteConfirm(null);
  }, []);

  const getStatusIcon = (status: UploadStatus['status']) => {
    switch (status) {
      case 'compressing':
      case 'uploading':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const safeImages = images ?? [];
  const safeUploadStatuses = uploadStatuses ?? [];

  return (
    <div className="space-y-3">
      {safeImages.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {(safeImages).map((img, index) => (
            <div key={`img-${index}`} className="relative group">
              <div
                className="w-20 h-20 rounded-lg border overflow-hidden bg-gray-50 cursor-pointer"
                onClick={() => window.open(img, '_blank')}
              >
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute top-1 left-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {index + 1}
              </div>
              <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-3 h-3 text-white" />
              </div>
              {!disabled && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(index);
                  }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-100 shadow-sm hover:bg-red-600 z-10"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {safeImages.length < maxCount && !disabled && (
        <div
          className={`w-full h-32 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isDragging
              ? 'border-blue-500 bg-blue-50'
              : isUploading
              ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (!isUploading && fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <Loader2 className="w-8 h-8 text-gray-400 animate-spin mb-2" />
              <span className="text-sm text-gray-500">上传中...</span>
            </div>
          ) : (
            <>
              <div className="text-4xl text-gray-300 mb-2">+</div>
              <span className="text-sm text-gray-500">
                点击上传或<span className="text-red-500 font-medium">复制粘贴图片</span>上传
              </span>
              <span className="text-xs text-gray-400 mt-1">不超过{maxSizeMB}M</span>
            </>
          )}
        </div>
      )}


      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        multiple
        onChange={handleFileSelect}
        disabled={isUploading}
        className="hidden"
      />


      {safeUploadStatuses.length > 0 && (
        <div className="space-y-2">
          {(safeUploadStatuses).map(status => (
            <div
              key={status.id}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                status.status === 'error'
                  ? 'bg-red-50 text-red-700'
                  : status.status === 'success'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-blue-50 text-blue-700'
              }`}
            >
              {getStatusIcon(status.status)}
              <span className="flex-1">{status.message}</span>
            </div>
          ))}
        </div>
      )}

      {showDeleteConfirm !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900 mb-2">确认删除</h3>
            <p className="text-gray-600 mb-4">确定要删除这张图片吗？删除后将无法恢复。</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
